import React from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import Input from '../ui/input'
import { useAuth } from '../../context/auth'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import { getOverdueReminderEmailSettings, updateReminders } from '../../api'
import { useAlerts } from '../../context/alerts'
import IconButton from '../ui/button/icon'
import { ReactComponent as TrashIcon } from '../../static/images/trash.svg'
import { ReactComponent as PlusIcon } from '../../static/images/plus.svg'
import TextArea from '../ui/input/textArea'
import './styles.scss'
import Alert from '../alerts/inline-alert'
import UpgradeToPremiumLink from '../billing/UpgradeToPremiumLink'

const RemindersForm = () => {
  const { user, mutate } = useAuth()
  const { addAlert } = useAlerts()
  const { data: emailSettings } = useSWR('/api/user/overdue-reminder-email', getOverdueReminderEmailSettings)
  const { trigger: triggerUpdate, isMutating: isLoading } = useSWRMutation(
    '/api/user/reminders',
    updateReminders
  )
  const [pristine, setPristine] = React.useState(true)
  const [values, setValues] = React.useState<Array<{ daysAfterDue: number }>>(user?.reminders || [])
  const [emailContent, setEmailContent] = React.useState('')

  const canEditEmail = emailSettings?.canEdit ?? false
  const defaultTemplate = emailSettings?.defaultTemplate ?? ''

  React.useEffect(() => {
    if (user?.reminders) {
      setValues(user.reminders)
      setPristine(true)
    }
  }, [user?.reminders])

  React.useEffect(() => {
    if (emailSettings === undefined) {
      return
    }
    const initial =
      emailSettings.content?.trim() ||
      user?.overdueReminderEmailContent?.trim() ||
      emailSettings.defaultTemplate ||
      ''
    setEmailContent(initial)
  }, [emailSettings, user?.overdueReminderEmailContent])

  if (!user) {
    return null
  }

  const onChangeValues = (newValues: Array<{ daysAfterDue: number }>) => {
    setValues(newValues)
    setPristine(false)
  }

  const addNewReminder = () => {
    const previousDays = values.length > 0 ? values[values.length - 1].daysAfterDue : 0
    const daysAfterDue = values.length > 0 ? Math.min(30, previousDays + 3) : 1
    onChangeValues([...values, { daysAfterDue }])
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newValues = [...values]
    newValues[index].daysAfterDue = parseInt(e.target.value, 10)
    onChangeValues(newValues)
  }

  const removeReminder = (index: number) => {
    const newValues = [...values]
    newValues.splice(index, 1)
    onChangeValues(newValues)
  }

  const handleEmailContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmailContent(e.target.value)
    setPristine(false)
  }

  const resetEmailToDefault = () => {
    setEmailContent(defaultTemplate)
    setPristine(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const body: {
        reminders: Array<{ daysAfterDue: number }>
        overdueReminderEmailContent?: string | null
      } = { reminders: values }
      if (canEditEmail && values.length >= 1) {
        body.overdueReminderEmailContent = emailContent.trim() || null
      }
      const res = await triggerUpdate({ body })
      await mutate()
      if (res?.data) {
        setPristine(true)
        addAlert({ type: 'success', message: 'Reminder settings updated' })
      }
    } catch (error: any) {
      addAlert({
        type: 'error',
        message: error?.message || 'Could not update reminder settings',
      })
    }
  }

  const maxRemindersReached = values && values.length >= 2
  const hasReminders = values.length >= 1
  const displayEmailContent =
    emailContent.trim() || defaultTemplate.trim() || (emailSettings === undefined ? 'Loading…' : '')

  return (
    <form onSubmit={handleSubmit}>
      {values?.map((reminder, index) => (
        <div key={index} className='row d-flex align-items-center mb-3'>
          <div className='col small-input'>
            <Input
              type='number'
              min={1}
              max={30}
              value={reminder.daysAfterDue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, index)}
            />
          </div>
          <div className='col-2'>{`day${reminder.daysAfterDue > 1 ? 's' : ''} after due date`}</div>
          <div className='col-1'>
            <IconButton icon={<TrashIcon height={20} width={20} />} onClick={() => removeReminder(index)} />
          </div>
        </div>
      ))}
      <Button
        type='button'
        variant={ButtonVariant.LIGHT}
        size={ButtonSize.DEFAULT}
        onClick={addNewReminder}
        disabled={maxRemindersReached}
        icon={<PlusIcon width={20} height={20} />}
        iconPosition='start'
      >
        Add reminder
      </Button>
      <div className='my-2' />
      {maxRemindersReached && (
        <Alert message='You can have a maximum of 2 reminders active at the same time.' />
      )}

      {hasReminders && (
        <div className='mt-4 pt-4 border-top'>
          <h3 className='h6 mb-1'>Edit Email Content</h3>
          {canEditEmail && (
            <>
              <p className='small text-body-secondary mb-2'>
                Optional placeholders, these will get replaced by the corresponding values:{' '}
                <code>{'{{clientName}}'}</code>, <code>{'{{invoiceCode}}'}</code>, <code>{'{{invoiceAmount}}'}</code>,{' '}
                <code>{'{{dueDate}}'}</code>, <code>{'{{providerName}}'}</code>
              </p>
              <p className='small text-body-secondary mb-2'>
                All emails will have at the bottom a button to see the invoice.
              </p>
            </>
          )}
          <TextArea
            label='Email message'
            name='overdueReminderEmailContent'
            className='form-control'
            rows={8}
            value={canEditEmail ? emailContent : displayEmailContent}
            onChange={canEditEmail ? handleEmailContentChange : undefined}
            readOnly={!canEditEmail}
            disabled={!canEditEmail}
          />
          {canEditEmail ? (
            <button type='button' className='btn btn-link btn-sm ps-0 mt-1' onClick={resetEmailToDefault}>
              Reset to default template
            </button>
          ) : (
            <UpgradeToPremiumLink label='Upgrade to Premium to edit' className='mt-2' />
          )}
          <div className='d-flex mt-4'>
            <Button size={ButtonSize.LARGE} disabled={pristine || isLoading} type='submit' loading={isLoading}>
              Save
            </Button>
          </div>
        </div>
      )}
    </form>
  )
}

export default RemindersForm
